import api from "./api";

// Fee / income reference table. GET routes are public on the backend (no
// auth/permission middleware) — the "view_fees" gate on the admin nav/route
// is frontend-only. Writes are RBAC-gated (create_fees / edit_fees /
// delete_fees).
//
// Response-shape note: index returns a plain array; show/store/update wrap
// the single row in an outer array (store/update also add a `message` key
// inside it) — unwrap() peels that so callers get a plain object.
const unwrap = (data) => (Array.isArray(data) ? data[0] : data);

// GET /api/fee-details — optional { ledger_code } filter. Returns a plain
// array of fees: {id, description, fee, ledger_code, created_at, updated_at}.
export const listFees = (params = {}) =>
  api.get("/api/fee-details", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/fee-details/{id}
export const getFee = (id) => api.get(`/api/fee-details/${id}`).then((r) => unwrap(r.data));

// POST /api/fee-details  { description, fee, ledger_code } — permission:
// create_fees. `description` must be unique among fee rows. Returns the
// created fee (with an extra `message` field).
export const createFee = (payload) =>
  api.post("/api/fee-details", payload).then((r) => unwrap(r.data));

// PUT /api/fee-details/{id}  { description, fee, ledger_code } — permission:
// edit_fees. The backend also accepts PATCH for a partial update (same
// controller method), but the admin edit form always submits the full row.
export const updateFee = (id, payload) =>
  api.put(`/api/fee-details/${id}`, payload).then((r) => unwrap(r.data));

// Fee create requests (IncomeRequestController) — a Bursar's POST /fee-details
// above doesn't create the fee directly (see createFee's docstring: the
// backend routes it here instead when the caller has submit_income_requests
// but not approve_income_requests). These rows live in `income_requests`,
// separate from `income`, until a Director/Admin approves one — approving
// inserts it into `income` and is what actually makes it show up in
// listFees(). Unlike fee-details, these responses are plain (unwrapped)
// objects/arrays — no unwrap() needed.

// GET /api/fee-request-approvals — permission: view_income_request_approvals
// (Director/Admin). Defaults to status=Pending server-side when no `status`
// param is given; also accepts { search }. Returns: {id, description, fee,
// ledger_code, status_id, status, income_id, requested_by, requested_by_name,
// approved_by, approved_by_name, approved_at, rejected_by, rejected_by_name,
// rejected_at, rejection_note, created_at, updated_at}[].
export const listFeeRequestApprovals = (params = {}) =>
  api.get("/api/fee-request-approvals", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/fee-request-approvals/{id} — permission: view_income_request_approvals.
// Single row, same shape as a listFeeRequestApprovals entry — used for the
// approvals detail modal's "View" action so it reflects the request's
// current state rather than the (possibly stale) row already in the list.
export const getFeeRequestApproval = (id) =>
  api.get(`/api/fee-request-approvals/${id}`).then((r) => r.data);

// GET /api/fee-requests/my-submissions — permission: submit_income_requests
// (Bursar). Optional { status } filter. Lets a Bursar track the fee requests
// they've submitted through their lifecycle (Pending -> Approved/Rejected) —
// same row shape as listFeeRequestApprovals.
export const listMyFeeRequests = (params = {}) =>
  api.get("/api/fee-requests/my-submissions", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/fee-request-approvals/{id}/approve — permission:
// approve_income_requests. 422s if the request isn't currently Pending, or if
// a fee with the same description was created in the meantime. Inserts the
// row into `income` and flips the request to "Approved".
export const approveFeeRequest = (id) =>
  api.post(`/api/fee-request-approvals/${id}/approve`).then((r) => r.data);

// POST /api/fee-request-approvals/{id}/reject — { rejection_note (required,
// max 1000) }, permission: reject_income_requests.
export const rejectFeeRequest = (id, rejectionNote) =>
  api.post(`/api/fee-request-approvals/${id}/reject`, { rejection_note: rejectionNote }).then((r) => r.data);
